import { InteractionRequiredAuthError } from '@azure/msal-browser';

/**
 * Minimal duck-typed stand-in for MSAL's `PublicClientApplication`, used only
 * in E2E runs (see `src/index.js`) so Playwright specs can drive the app as an
 * already-authenticated user without a real Azure External ID tenant/redirect
 * flow. Only implements the surface `@azure/msal-react`'s `MsalProvider`/hooks
 * and this app's own MSAL calls (`acquireTokenSilent`, `loginRedirect`,
 * `logoutRedirect`) actually touch.
 *
 * Activated only when `window.__E2E_MOCK_AUTH__` is set, which happens only via
 * Playwright's `page.addInitScript` (see `e2e/support/mockAuth.ts`) — never in
 * a real deployment.
 */
export class FakeMsalInstance {
    constructor(initialAccount) {
        this._account = initialAccount ?? null;
        this._callbacks = new Map();
        this._nextCallbackId = 1;
    }

    // ── Lifecycle: MsalProvider calls these directly ──
    initializeWrapperLibrary() {}

    getLogger() {
        const noop = () => {};
        const logger = { verbose: noop, info: noop, warning: noop, error: noop, clone: () => logger };
        return logger;
    }

    async initialize() {}

    async handleRedirectPromise() {
        return null;
    }

    addEventCallback(callback) {
        const id = String(this._nextCallbackId++);
        this._callbacks.set(id, callback);
        return id;
    }

    removeEventCallback(id) {
        this._callbacks.delete(id);
    }

    // ── Account state ──
    getAllAccounts() {
        return this._account ? [this._account] : [];
    }

    getActiveAccount() {
        return this._account;
    }

    setActiveAccount(account) {
        this._account = account;
    }

    // ── Token acquisition ──
    async acquireTokenSilent() {
        if (!this._account) {
            throw new InteractionRequiredAuthError('no_account', 'No active account for silent token acquisition.');
        }
        return { accessToken: 'e2e-mock-access-token', account: this._account };
    }

    async acquireTokenRedirect() {
        // Tests seed an authenticated account up front; interactive redirect is
        // never expected to be exercised in E2E runs.
    }

    async loginRedirect() {}

    async logoutRedirect() {
        this._account = null;
    }
}
